import CheckCircle from "@mui/icons-material/CheckCircle";
import Send from "@mui/icons-material/Send";
import Warning from "@mui/icons-material/Warning";
import { Button, CircularProgress, Stack, Tooltip } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useUpdateSubmissionStatus } from "hooks/useSubmissionMutations";
import { useState } from "react";

import { ForceCompleteDialog } from "./ForceCompleteDialog";

interface UpdateSubmissionStatusButtonProps {
  submissionId?: string;
  tenantId: string;
  currentStatus?: SubmissionStatus;
  isStaff: boolean;
  isEditMode: boolean;
  isSendToReviewersDisabled: boolean;
  loading: boolean;
  formLoading: boolean;
  onMoveToReview: () => void;
  onSuccess?: () => void;
  onForceCompleteSuccess?: () => void;
  statusUpdatesDisabled?: boolean;
  markFilingCompleteDisabledTooltip?: string;
  forceCompleteDisabledTooltip?: string;
}

export function UpdateSubmissionStatusButton({
  submissionId,
  tenantId,
  currentStatus,
  isStaff,
  isEditMode,
  isSendToReviewersDisabled,
  loading,
  formLoading,
  onMoveToReview,
  onSuccess,
  onForceCompleteSuccess,
  statusUpdatesDisabled,
  markFilingCompleteDisabledTooltip,
  forceCompleteDisabledTooltip,
}: UpdateSubmissionStatusButtonProps) {
  const alerts = useAlerts();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [forceCompleteDialogOpen, setForceCompleteDialogOpen] = useState(false);
  const [updateSubmissionStatus, { loading: updating }] = useUpdateSubmissionStatus();
  const markCompleteDisabled = updating || Boolean(statusUpdatesDisabled);
  const markCompleteDisabledTooltip = markFilingCompleteDisabledTooltip ?? "";
  const forceCompleteTooltip =
    forceCompleteDisabledTooltip ?? markFilingCompleteDisabledTooltip ?? "";
  const disableCompletionTooltipInactive = !statusUpdatesDisabled;

  const handleClick = () => {
    if (markCompleteDisabled) return;
    setConfirmDialogOpen(true);
  };

  const handleForceCompleteClick = () => {
    if (markCompleteDisabled) return;
    setForceCompleteDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!submissionId) return;
    try {
      await updateSubmissionStatus({
        variables: {
          input: {
            submissionId,
            submissionStatus: SubmissionStatus.FilingComplete,
          },
        },
      });

      alerts.success("Submission status updated to Filing Complete");
      setConfirmDialogOpen(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update submission status:", error);
      alerts.error("Failed to update submission status");
    }
  };

  // Show "🎉 Filing Complete" text if status is FILING_COMPLETE
  if (currentStatus === SubmissionStatus.FilingComplete) {
    return (
      <Button
        variant="text"
        disabled
        sx={{
          color: "text.primary",
          fontSize: "1rem",
          fontWeight: 500,
          cursor: "default",
          "&.Mui-disabled": {
            color: "text.primary",
          },
        }}
      >
        🎉 Filing Complete
      </Button>
    );
  }

  // Helper to determine if Force Complete should be shown
  // Note: We've already returned early for FILING_COMPLETE status above
  const canShowForceComplete =
    isEditMode && isStaff && currentStatus !== SubmissionStatus.NotReporting;

  // Show "Mark as Filing Complete" button if status is PROCESSING_SUBMISSION and user is staff
  if (isEditMode && currentStatus === SubmissionStatus.ProcessingSubmission && isStaff) {
    return (
      <>
        <Stack direction="row" spacing={1}>
          {canShowForceComplete && (
            <Tooltip
              title={forceCompleteTooltip}
              disableHoverListener={disableCompletionTooltipInactive}
              disableFocusListener={disableCompletionTooltipInactive}
              disableTouchListener={disableCompletionTooltipInactive}
            >
              <span>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<Warning />}
                  onClick={handleForceCompleteClick}
                  disabled={markCompleteDisabled}
                  sx={{ textTransform: "uppercase" }}
                >
                  Force Complete
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={markCompleteDisabledTooltip}
            disableHoverListener={disableCompletionTooltipInactive}
            disableFocusListener={disableCompletionTooltipInactive}
            disableTouchListener={disableCompletionTooltipInactive}
          >
            <span>
              <Button
                variant="contained"
                color="success"
                startIcon={updating ? <CircularProgress size={20} /> : <CheckCircle />}
                onClick={handleClick}
                disabled={markCompleteDisabled}
                sx={{ textTransform: "uppercase" }}
              >
                Mark as filing complete
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleConfirm}
          header="Confirm Status Update"
          info="Are you sure you want to mark this submission as Filing Complete? This action cannot be undone."
          confirmText="Mark as Complete"
          cancelText="Cancel"
        />

        {submissionId && (
          <ForceCompleteDialog
            open={forceCompleteDialogOpen}
            onClose={() => setForceCompleteDialogOpen(false)}
            submissionId={submissionId}
            tenantId={tenantId}
            onSuccess={onForceCompleteSuccess}
          />
        )}
      </>
    );
  }

  // "Move to Review" only makes sense pre-customer-review; the regular Save button covers in-review edits.
  const canShowMoveToReview =
    !currentStatus ||
    currentStatus === SubmissionStatus.NotStarted ||
    currentStatus === SubmissionStatus.InProgress ||
    (isStaff && currentStatus === SubmissionStatus.EncampInitialReview);

  if (!canShowMoveToReview && !canShowForceComplete) {
    return null;
  }

  const buttonLabel = isStaff ? "Save & Move to Review" : "Move to Review";

  return (
    <>
      <Stack direction="row" spacing={1}>
        {canShowForceComplete && (
          <Tooltip
            title={forceCompleteTooltip}
            disableHoverListener={disableCompletionTooltipInactive}
            disableFocusListener={disableCompletionTooltipInactive}
            disableTouchListener={disableCompletionTooltipInactive}
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Warning />}
                onClick={handleForceCompleteClick}
                disabled={markCompleteDisabled}
                sx={{ textTransform: "uppercase" }}
              >
                Force Complete
              </Button>
            </span>
          </Tooltip>
        )}
        {canShowMoveToReview && (
          <Button
            variant="contained"
            disabled={isSendToReviewersDisabled || loading || formLoading}
            endIcon={<Send />}
            onClick={onMoveToReview}
            sx={{ textTransform: "uppercase" }}
          >
            {buttonLabel}
          </Button>
        )}
      </Stack>

      {submissionId && (
        <ForceCompleteDialog
          open={forceCompleteDialogOpen}
          onClose={() => setForceCompleteDialogOpen(false)}
          submissionId={submissionId}
          tenantId={tenantId}
          onSuccess={onForceCompleteSuccess}
        />
      )}
    </>
  );
}
